import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  IconButton,
  LinearProgress,
  Pagination,
  Tooltip,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { CreateStock, EditStock, StockFilters } from '../components';
import { ClearFiltersButton, CommonList, ListSelectionMode } from '../../common/components';
import type { Stock } from '../../../types/stock';
import { useStockData } from '../useStockData';

export default function StockViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state } = useLocation();
  const companyId = id ? parseInt(id) : null;

  const returnPath = (state as { returnPath?: string } | null)?.returnPath;

  const { data, pagination, filters, crud, dialogs } = useStockData({ companyId });

  if (data.isFirstLoad) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (data.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {data.error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 0.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(returnPath ?? `/companies/${companyId}/home`)}
          size="small"
        >
          {returnPath ? 'Назад' : 'На головну'}
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          Склади
        </Typography>
      </Box>

      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
          }}
        >
          <Tooltip title="Показати/сховати фільтри">
            <IconButton
              size="small"
              onClick={() => filters.setShowFilters(!filters.showFilters)}
              color={filters.showFilters || filters.hasActiveFilters ? 'primary' : 'default'}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => dialogs.setCreateDialogOpen(true)}
          >
            Створити
          </Button>
        </Box>
      </Box>

      <StockFilters
        show={filters.showFilters}
        filterName={filters.filterName}
        onFilterNameChange={filters.setFilterName}
        showOnlyActive={filters.showOnlyActive}
        onShowOnlyActiveChange={filters.setShowOnlyActive}
        onClearFilters={filters.handleClearFilters}
      />

      {data.loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Content */}
      <Box sx={{ opacity: data.loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        {data.stocks.length === 0 && !data.loading ? (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 6 },
              textAlign: 'center',
              backgroundColor: 'background.default',
              border: '1px dashed',
              borderColor: 'divider',
            }}
          >
            {filters.hasActiveFilters ? (
              <>
                <Typography variant="h6" gutterBottom>
                  За вашим пошуком складів не знайдено
                </Typography>
                <ClearFiltersButton onClick={filters.handleClearFilters} />
              </>
            ) : (
              <>
                <Typography variant="h5" gutterBottom>
                  Складів ще немає
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Створіть свій перший склад, щоб почати
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => dialogs.setCreateDialogOpen(true)}
                >
                  Створити
                </Button>
              </>
            )}
          </Paper>
        ) : (
          <>
            <CommonList<Stock>
              items={data.stocks}
              toListItem={(stock) => ({ id: stock.id, primary: stock.name, archived: !!stock.deactivatedDate })}
              onDelete={crud.handleDeleteStock}
              onRestore={crud.handleRestoreStock}
              onEdit={crud.handleEditStock}
              selectionMode={ListSelectionMode.VIEW}
              showSelection={false}
              emptyMessage="Склади не знайдено"
              onItemClick={(item) => navigate(`/companies/${companyId}/stocks/${item.id}`)}
            />

            {pagination.count > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  {...pagination}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Create Stock Dialog */}
      {companyId && (
        <CreateStock
          open={dialogs.createDialogOpen}
          companyId={companyId}
          onClose={() => dialogs.setCreateDialogOpen(false)}
          onSubmit={crud.handleCreateStock}
        />
      )}

      {/* Edit Stock Dialog */}
      <EditStock
        open={dialogs.editDialogOpen}
        stock={dialogs.stockToEdit}
        onClose={() => dialogs.setEditDialogOpen(false)}
        onSubmit={crud.handleUpdateStock}
      />

      {dialogs.confirmDialog}
    </Box>
  );
}
